import React, { useState, useEffect } from "react";
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  message, 
  Typography, 
  Spin, 
  Modal, 
  Timeline,
  Space,
  Alert,
  Tag,
  Divider
} from "antd";
import { 
  EditOutlined, 
  SaveOutlined, 
  HistoryOutlined, 
  RollbackOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import TextArea from "antd/es/input/TextArea";

declare const API_BASE_URL: string;

const { Title, Text, Paragraph } = Typography;

interface PDPMPrompt {
  id: string;
  prompt_type: string;
  prompt_text: string;
  modified_by: string;
  modified_at: string;
  created_at: string;
}

interface PDPMPromptHistoryItem {
  history_id: string;
  prompt_id: string;
  prompt_type: string;
  prompt_text: string;
  modified_by: string;
  modified_at: string;
  recorded_at: string;
}

interface PromptsResponse {
  prompts: Record<string, PDPMPrompt>;
}

const promptTypeInfo = {
  primary_diagnosis: {
    title: "Primary Diagnosis",
    description: "Analyzes codes to recommend the best primary diagnosis and alternatives",
    icon: "🏆",
    color: "gold",
    variables: [
      "{medical_document_text} - The full medical document text",
      "{icd10_codes} - List of ICD-10 codes"
    ]
  },
      secondary_diagnoses: {
      title: "Secondary Diagnoses",
      description: "Analyzes codes for documentation as secondary diagnoses",
    icon: "📋",
    color: "green",
    variables: [
      "{medical_document_text} - The full medical document text",
      "{icd10_codes} - List of ICD-10 codes"
    ]
  }
};

const PromptHistory: React.FC<{
  promptType: string;
  onRevert: (item: PDPMPromptHistoryItem) => void;
  onClose: () => void;
}> = ({ promptType, onRevert, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [history, setHistory] = useState<PDPMPromptHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/pdpm-prompts/${promptType}/history`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to fetch history");
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error("Error fetching history:", error);
      message.error("Error fetching prompt history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [promptType]);

  const info = promptTypeInfo[promptType as keyof typeof promptTypeInfo];

  return (
    <Modal
      title={`Prompt History - ${info?.title || promptType}`}
      open={true}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Spin spinning={loading}>
        <Timeline
          items={history.map((item) => ({
            color: 'blue',
            key: item.history_id,
            children: (
              <div style={{ marginBottom: 20 }}>
                <Title level={5}>
                  {new Date(item.modified_at).toLocaleString()}
                </Title>
                <Text type="secondary">
                  Modified by: {item.modified_by}
                </Text>
                <div style={{ marginTop: 10 }}>
                  <Paragraph
                    style={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}
                  >
                    {item.prompt_text}
                  </Paragraph>
                </div>
                <Button
                  type="primary"
                  icon={<RollbackOutlined />}
                  onClick={() => onRevert(item)}
                  style={{ marginTop: 10 }}
                >
                  Revert to this version
                </Button>
              </div>
            ),
          }))}
        />
      </Spin>
    </Modal>
  );
};

export const PDPMPromptEditor: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [prompts, setPrompts] = useState<PromptsResponse | null>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [showHistory, setShowHistory] = useState<string | null>(null);

  const fetchPrompts = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/pdpm-prompts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to fetch prompts");
      const data = await response.json();
      setPrompts(data);
    } catch (error) {
      message.error("Error fetching PDPM prompts");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const handleSavePrompt = async (promptType: string, promptText: string) => {
    setLoading(prev => ({ ...prev, [promptType]: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/pdpm-prompts/${promptType}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt_text: promptText }),
      });

      if (!response.ok) throw new Error("Failed to save prompt");

      const updatedData = await response.json();
      setPrompts(updatedData);
      message.success(`${promptTypeInfo[promptType as keyof typeof promptTypeInfo]?.title} prompt saved successfully!`);
    } catch (error) {
      message.error("Error saving prompt. Please try again.");
      console.error(error);
    } finally {
      setLoading(prev => ({ ...prev, [promptType]: false }));
    }
  };

  const handleRevertPrompt = async (historyItem: PDPMPromptHistoryItem) => {
    const promptType = historyItem.prompt_type;
    setLoading(prev => ({ ...prev, [promptType]: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/pdpm-prompts/${promptType}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt_text: historyItem.prompt_text }),
      });
      if (!response.ok) throw new Error("Failed to revert prompt");
      const updatedData = await response.json();
      setPrompts(updatedData);
      message.success("Successfully reverted to previous version!");
      setShowHistory(null);
    } catch (error) {
      message.error("Error reverting prompt. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, [promptType]: false }));
    }
  };

  if (!prompts) {
    return <div style={{ textAlign: 'center', padding: '48px' }}><Spin size="large" /></div>;
  }

  const promptOrder = ['primary_diagnosis', 'secondary_diagnoses'];

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2}>PDPM Prompt Management</Title>
        <Paragraph type="secondary">
          Manage prompts used for PDPM ranking analysis. These prompts control how the AI analyzes ICD-10 codes for SNF reimbursement optimization.
        </Paragraph>
      </div>

      <Alert
        message="Important"
        description="Changes to these prompts will immediately affect how PDPM analysis is performed. Test thoroughly before making changes in production."
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[16, 16]}>
        {promptOrder.map((promptType) => {
          const prompt = prompts.prompts[promptType];
          const info = promptTypeInfo[promptType as keyof typeof promptTypeInfo];
          
          if (!prompt || !info) return null;

          return (
            <Col key={promptType} xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <span style={{ fontSize: '20px' }}>{info.icon}</span>
                    <span>{info.title}</span>
                    <Tag color={info.color}>{promptType}</Tag>
                  </Space>
                }
                extra={
                  <Button 
                    icon={<HistoryOutlined />} 
                    onClick={() => setShowHistory(promptType)}
                    type="text"
                  >
                    History
                  </Button>
                }
                style={{ height: '100%' }}
              >
                <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
                  {info.description}
                </Paragraph>

                <Divider orientation="left" plain>
                  <Text type="secondary">Available Variables</Text>
                </Divider>
                
                <div style={{ marginBottom: '16px', background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                  {info.variables.map((variable, index) => (
                    <div key={index} style={{ marginBottom: index < info.variables.length - 1 ? '4px' : 0 }}>
                      <code style={{ color: '#1890ff' }}>{variable}</code>
                    </div>
                  ))}
                </div>

                <Divider orientation="left" plain>
                  <Text type="secondary">Prompt Template</Text>
                </Divider>

                <TextArea
                  value={prompt.prompt_text}
                  onChange={(e) => {
                    setPrompts(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        prompts: {
                          ...prev.prompts,
                          [promptType]: {
                            ...prev.prompts[promptType],
                            prompt_text: e.target.value
                          }
                        }
                      };
                    });
                  }}
                  rows={12}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />

                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSavePrompt(promptType, prompt.prompt_text)}
                    loading={loading[promptType]}
                    block
                  >
                    Save {info.title} Prompt
                  </Button>
                </div>

                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Last modified: {new Date(prompt.modified_at).toLocaleString()} by {prompt.modified_by}
                  </Text>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {showHistory && (
        <PromptHistory
          promptType={showHistory}
          onRevert={handleRevertPrompt}
          onClose={() => setShowHistory(null)}
        />
      )}
    </div>
  );
}; 